import React, {useState, useEffect, useMemo} from 'react'
import {Dropdown} from 'react-bootstrap'
import Select from 'react-select'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import axios from 'axios'
import {useAuth} from '../../../modules/auth'
import {Form, Formik} from 'formik'
import {RootState} from '../../../redux/store'
import {useSelector, useDispatch} from 'react-redux'
import {
  allAppointmentsCurrentPage,
  allAppointmentsFilterData,
  allAppointmentsRowsPerPage,
} from '../../../redux/tableSlice/tableSlice'
import toaster from '../../../../Utils/toaster'
import {useInterpreters, useRequesters, useLanguages, useServiceTypes, useCommunicationTypes, useCustomers} from '../../../hooks/useGlobalData'
import LazySelect from '../../../components/LazySelect'
import {
  APPOINTMENT_STATUS_FILTER_OPTIONS,
  getFilteredAppointmentStatusOptions,
} from '../../../../Utils/appointmentStatusUtils'
import {ApplicableSubAccountFilter} from './ApplicableSubAccountFilter'
import {canFilterByRequester, hasActiveAppointmentFilter} from '../../../../Utils/appointmentFilterUtils'

type Props = {
  setAllAppointments: any
  setCurrentPage: any
  filterData: any
  fetchAllAppointments: any
  searchQuery: any
  setTotalPages: React.Dispatch<React.SetStateAction<number>>
}
export function AppointmentViewTableFilterDropdown({
  setAllAppointments,
  setCurrentPage,
  searchQuery,
  filterData,
  fetchAllAppointments,
  setTotalPages,
}: Props) {
  const {currentUser} = useAuth()
  const [showDropdownMenu, setShowDropdownMenu] = useState(false)
  const dispatch = useDispatch()
  const {rowsPerPage} = useSelector((state: any) => ({
    rowsPerPage: state.table.allAppointments[0].rowsPerPage,
  }))
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  useEffect(() => {
    setIsFilterApplied(hasActiveAppointmentFilter(filterData))
  }, [filterData])

  // Use global cached data
  const {data: customersData, isLoading: isFetchingCustomer} = useCustomers()
  const {data: languagesData, isLoading: isFetchingLanguage} = useLanguages()
  const {data: serviceTypesData, isLoading: isFetchingServicetypes} = useServiceTypes()
  const {data: communicationTypesData, isLoading: isFetchingCommunicationtypes} = useCommunicationTypes()
  const {data: interpretersData, isLoading: isFetchingInterpreter} = useInterpreters()
  const {data: requestersData, isLoading: isFetchingRequester} = useRequesters()

  const languageOptions = useMemo(() => 
    (languagesData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [languagesData])

  const serviceTypeOptions = useMemo(() => 
    (serviceTypesData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [serviceTypesData])

  const communicationTypeOptions = useMemo(() => 
    (communicationTypesData ?? [])
      .filter((item: any) => item.identification !== 'ON_DEMAND')
      .map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [communicationTypesData])

  const customerOptions = useMemo(() => 
    (customersData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [customersData])
  
  const interpreterOptions = useMemo(() => 
    (interpretersData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [interpretersData])
  
  const requesterOptions = useMemo(() => 
    (requestersData ?? []).map((d: any) => ({value: d.key ?? '', label: d.value ?? ''}))
  , [requestersData])
  const [data, setdata] = useState(filterData)
  const [subAccounts, setSubAccounts] = useState<any[]>([])
  const [selectedSubAccount, setSelectedSubAccount] = useState<any>()
  const [isLoadingSubAccounts, setIsLoadingSubAccounts] = useState(false)

  useEffect(() => {
    setdata(filterData)
  }, [filterData])

  const handleSubmit = (values: any) => {
    try {
      dispatch(allAppointmentsCurrentPage(1))
      fetchAllAppointments(values, searchQuery, 1, rowsPerPage)
      setShowDropdownMenu(false)
      setIsFilterApplied(hasActiveAppointmentFilter(values))
      dispatch(allAppointmentsFilterData({...values}))
    } catch (error) {
      console.error(error)
      toaster('error', 'Server Error')
    }
  }

  // Fetch sub accounts when customers are selected
  const fetchSubAccounts = async (customers: any) => {
    setIsLoadingSubAccounts(true)
    try {
      const values = customers.map((item: any) => item.value)
      const url = `${process.env.REACT_APP_API_URL}/customer/sub-accounts/shortlist-multi`
      const response = await axios.post<{data: any[]}>(url, values)
      const list = response.data.data || []
      const options = list.map((d) => ({
        value: d.code,
        label: d.name,
        id: d.code,
      }))
      setSubAccounts([...options])
    } catch (err) {
      console.error('Error fetching sub‑accounts:', err)
    } finally {
      setIsLoadingSubAccounts(false)
      setSelectedSubAccount(null)
    }
  }
  const status = [...APPOINTMENT_STATUS_FILTER_OPTIONS]

  // Interpreters and Requesters options are now defined above using global hooks
    
  const getFilteredStatusOptions = () => getFilteredAppointmentStatusOptions(currentUser?.result.userType)

  const canceledStatusOptions = [
    {value: '0', label: 'Normal Cancellation'},
    {value: '1', label: 'Interpreter No Show'},
    {value: '2', label: 'Payable'},
    {value: '3', label: 'Billable'},
    {value: '4', label: 'Billable & Payable'},
  ]

  const aiVosStatusOptions = [
    {value: '6', label: 'AI Approved'},
    {value: '5', label: 'AI Dispute'},
  ]

  const showCanceledStatusDropdown = currentUser?.result.userType === 'SYSTEM'
  const showAiVosStatusDropdown = currentUser?.result.userType === 'SYSTEM'
  const isRequester = currentUser?.result.userType === 'CONSUMER'
  const showRequesterFilter = canFilterByRequester(currentUser)

  const handleReset = async () => {
    setdata({
      accounts: [],
      langs: [],
      serviceTypes: [],
      communicationTypes: [],
      startDate: undefined,
      endDate: undefined,
      status: [],
      interpreters: [],
      requesters: [],
      subaccounts: [],
      canceledStatus: [],
      aiVosStatus: [],
    })

    dispatch(allAppointmentsCurrentPage(1))
    dispatch(allAppointmentsRowsPerPage(10))
    dispatch(
      allAppointmentsFilterData({
        accounts: [],
        langs: [],
        serviceTypes: [],
        communicationTypes: [],
        status: [],
        startDate: null,
        endDate: null,
        interpreters: [],
        requesters: [],
        subaccounts: [],
        billable: undefined,
        payable: undefined,
        aiVosStatus: [],
      })
    )
    fetchAllAppointments(
      {
        accounts: [],
        langs: [],
        serviceTypes: [],
        communicationTypes: [],
        startDate: undefined,
        endDate: undefined,
        status: [],
        interpreters: [],
        requesters: [],
        subaccounts: [],
        billable: undefined,
        payable: undefined,
        aiVosStatus: [],
      },
      searchQuery,
      1,
      10
    )
    setShowDropdownMenu(false)
    setIsFilterApplied(false)
    resetForm()
  }

  return (
    <>
      <Dropdown onToggle={(nextShow) => setShowDropdownMenu(nextShow)}>
        <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-filter'>Filter</Tooltip>}>
          <Dropdown.Toggle
            variant='primary'
            id='dropdown-basic'
            className={`btn btn-icon btn-sm no-caret ${isFilterApplied ? 'btn-light-danger' : ''}`}
          >
            <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-primary' />
          </Dropdown.Toggle>
        </OverlayTrigger>
        {showDropdownMenu && (
          <Dropdown.Menu className='p-4 w-350px' align='end'>
            <div className='px-4 pb-4'>
              <div className='fs-4 text-dark fw-semibolder'>Filter</div>
            </div>
            <Formik
              enableReinitialize
              initialValues={data}
              onSubmit={async (values, {setSubmitting, resetForm}) => {
                setSubmitting(true)
                setdata(values)
                handleSubmit(values)
              }}
            >
              {({
                isSubmitting,
                handleChange,
                handleBlur,
                setFieldTouched,
                setFieldValue,
                handleSubmit,
                resetForm,
                values,
                errors,
                touched,
                isValid,
                dirty,
                ...formik
              }) => (
                <Form>
                  <div className='separator border-gray-200'></div>
                  <div className='card px-4 mt-4'>
                    <div className='row flex-column'>
                      {/* Customer Dropdown */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='customerName' className='form-label fs-7 mb-1'>
                                  Customer Name(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values?.accounts?.length === customerOptions?.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue(
                                        'accounts',
                                        customerOptions?.map((option) => option.value)
                                      )
                                    } else {
                                      setFieldValue('accounts', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='accounts'
                                  options={!isFetchingCustomer ? customerOptions : []}
                                  placeholder='Select Customer(s)'
                                  isMulti
                                  isLoading={isFetchingCustomer}
                                  value={
                                    customerOptions?.filter(
                                      (option: any) =>
                                        Array.isArray(values.accounts) &&
                                        (values.accounts as string[]).includes(option.value)
                                    ) || []
                                  }
                                  onChange={async (selectedOptions: any[]) => {
                                    setFieldValue(
                                      'accounts',
                                      selectedOptions
                                        ? selectedOptions.map(
                                            (option: {value: any}) => option.value
                                          )
                                        : []
                                    )
                                    setFieldValue('subaccounts', [])
                                    if (!isRequester) {
                                      await fetchSubAccounts(selectedOptions || [])
                                    }
                                  }}
                                  onBlur={(e: any) => setFieldTouched('customers', true)}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <ApplicableSubAccountFilter
                        value={values.subaccounts}
                        selectedCustomerIds={values.accounts ?? []}
                        onChange={(subaccounts) => setFieldValue('subaccounts', subaccounts)}
                      />

                      {/* Sub Account Dropdown */}
                      {!isRequester && subAccounts.length > 0 && (
                        <div className='col-sm-12 col-md-12'>
                          <div className='mb-3'>
                            <div className='col-md-12'>
                              <div className='d-flex justify-content-between align-items-center'>
                                <div>
                                  <label htmlFor='subaccounts' className='form-label fs-7 mb-1'>
                                    Sub Account Name
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className='row g-4 g-xl-4'>
                              <div className='col-sm-12 col-md-12 col-lg-12'>
                                <div className='w-100'>
                                  <Select
                                    className='react-select-styled react-select-solid react-select-sm'
                                    classNamePrefix='react-select'
                                    isMulti
                                    isLoading={isLoadingSubAccounts}
                                    options={subAccounts}
                                    placeholder='Select Sub Account'
                                    value={selectedSubAccount}
                                    onChange={(selectedOption: any) => {
                                      setSelectedSubAccount(selectedOption)
                                      setFieldValue(
                                        'subaccounts',
                                        selectedOption
                                          ? selectedOption.map((option: any) => option.value)
                                          : []
                                      )
                                    }}
                                    styles={{
                                      control: (provided: any) => ({
                                        ...provided,
                                        width: '100%',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        border: '1px solid #e4e6ef',
                                      }),
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Requester Filter */}
                      {showRequesterFilter && (
                            <div className='col-sm-12 col-md-12'>
                              <div className='mb-3'>
                                <div className='col-md-12'>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <div>
                                      <label htmlFor='requesters' className='form-label fs-7 mb-1'>
                                        Requester(s)
                                      </label>
                                    </div>
                                    <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                      <input
                                        className='form-check-input'
                                        type='checkbox'
                                        checked={values?.requesters?.length === requesterOptions?.length}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFieldValue(
                                              'requesters',
                                              requesterOptions?.map((option: any) => option.value)
                                            )
                                          } else {
                                            setFieldValue('requesters', [])
                                          }
                                        }}
                                      />
                                      <label className='form-check-label' htmlFor='flexRadioLg1'>
                                        All
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                <div className='row g-4 g-xl-4'>
                                  <div className='col-sm-12 col-md-12 col-lg-12'>
                                    <div className='w-100'>
                                      <LazySelect
                                        name='requesters'
                                        options={!isFetchingRequester ? requesterOptions : []}
                                        placeholder='Select Requester(s)'
                                        isMulti
                                        isLoading={isFetchingRequester}
                                        value={
                                          requesterOptions?.filter(
                                            (option: any) =>
                                              Array.isArray(values.requesters) &&
                                              (values.requesters as string[]).includes(option.value)
                                          ) || []
                                        }
                                        onChange={(selectedOptions: any[]) => {
                                          setFieldValue(
                                            'requesters',
                                            selectedOptions
                                              ? selectedOptions.map((option: { value: any }) => option.value)
                                              : []
                                          )
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                      )}

                      {/* Interpreter Filter */}
                      {currentUser?.result.userType === 'SYSTEM' && (
                            <div className='col-sm-12 col-md-12'>
                              <div className='mb-3'>
                                <div className='col-md-12'>
                                  <div className='d-flex justify-content-between align-items-center'>
                                    <div>
                                      <label htmlFor='interpreters' className='form-label fs-7 mb-1'>
                                        Interpreter(s)
                                      </label>
                                    </div>
                                    <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                      <input
                                        className='form-check-input'
                                        type='checkbox'
                                        checked={values?.interpreters?.length === interpreterOptions?.length}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFieldValue(
                                              'interpreters',
                                              interpreterOptions?.map((option: any) => option.value)
                                            )
                                          } else {
                                            setFieldValue('interpreters', [])
                                          }
                                        }}
                                      />
                                      <label className='form-check-label' htmlFor='flexRadioLg1'>
                                        All
                                      </label>
                                    </div>
                                  </div>
                                </div>

                                <div className='row g-4 g-xl-4'>
                                  <div className='col-sm-12 col-md-12 col-lg-12'>
                                    <div className='w-100'>
                                      <LazySelect
                                        name='interpreters'
                                        options={!isFetchingInterpreter ? interpreterOptions : []}
                                        placeholder='Select Interpreter(s)'
                                        isMulti
                                        isLoading={isFetchingInterpreter}
                                        value={
                                          interpreterOptions?.filter(
                                            (option: any) =>
                                              Array.isArray(values.interpreters) &&
                                              (values.interpreters as string[]).includes(option.value)
                                          ) || []
                                        }
                                        onChange={(selectedOptions: any[]) => {
                                          setFieldValue(
                                            'interpreters',
                                            selectedOptions
                                              ? selectedOptions.map((option: { value: any }) => option.value)
                                              : []
                                          )
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                      )}

                      {/* Language Dropdown */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='languages' className='form-label fs-7 mb-1'>
                                  Language(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={values?.langs?.length === languageOptions?.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue(
                                        'langs',
                                        languageOptions?.map((option) => option.value)
                                      )
                                    } else {
                                      setFieldValue('langs', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='langs'
                                  options={!isFetchingLanguage ? languageOptions : []}
                                  placeholder='Select Language(s)'
                                  isMulti
                                  isLoading={isFetchingLanguage}
                                  value={
                                    languageOptions?.filter(
                                      (option: any) =>
                                        Array.isArray(values.langs) &&
                                        (values.langs as string[]).includes(option.value)
                                    ) || []
                                  }
                                  onChange={(selectedOptions: any[]) => {
                                    setFieldValue(
                                      'langs',
                                      selectedOptions
                                        ? selectedOptions.map(
                                            (option: {value: any}) => option.value
                                          )
                                        : []
                                    )
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Service Type Dropdown */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='serviceType' className='form-label fs-7 mb-1'>
                                  Service Type(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={
                                    values?.serviceTypes?.length === serviceTypeOptions?.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue(
                                        'serviceTypes',
                                        serviceTypeOptions?.map((option) => option.value)
                                      )
                                    } else {
                                      setFieldValue('serviceTypes', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='serviceTypes'
                                  options={!isFetchingServicetypes ? serviceTypeOptions : []}
                                  placeholder='Select : All'
                                  isMulti
                                  isLoading={isFetchingServicetypes}
                                  value={
                                    serviceTypeOptions?.filter(
                                      (option: any) =>
                                        Array.isArray(values.serviceTypes) &&
                                        (values.serviceTypes as string[]).includes(option.value)
                                    ) || []
                                  }
                                  onChange={(selectedOptions: any[]) => {
                                    setFieldValue(
                                      'serviceTypes',
                                      selectedOptions
                                        ? selectedOptions.map(
                                            (option: {value: any}) => option.value
                                          )
                                        : []
                                    )
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Communication Type Dropdown */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='communicationType' className='form-label fs-7 mb-1'>
                                  Communication Type(s)
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={
                                    values?.communicationTypes?.length ===
                                    communicationTypeOptions?.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue(
                                        'communicationTypes',
                                        communicationTypeOptions?.map((option) => option.value)
                                      )
                                    } else {
                                      setFieldValue('communicationTypes', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <LazySelect
                                  name='communicationTypes'
                                  options={
                                    !isFetchingCommunicationtypes ? communicationTypeOptions : []
                                  }
                                  placeholder='Select Communication Type(s)'
                                  isMulti
                                  isLoading={isFetchingCommunicationtypes}
                                  value={
                                    communicationTypeOptions?.filter(
                                      (option: any) =>
                                        Array.isArray(values.communicationTypes) &&
                                        (values.communicationTypes as string[]).includes(
                                          option.value
                                        )
                                    ) || []
                                  }
                                  onChange={(selectedOptions: any[]) => {
                                    setFieldValue(
                                      'communicationTypes',
                                      selectedOptions
                                        ? selectedOptions.map(
                                            (option: {value: any}) => option.value
                                          )
                                        : []
                                    )
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status Dropdown */}
                      <div className='col-sm-12 col-md-12'>
                        <div className='mb-3'>
                          <div className='col-md-12'>
                            <div className='d-flex justify-content-between align-items-center'>
                              <div>
                                <label htmlFor='status' className='form-label fs-7 mb-1'>
                                  Status
                                </label>
                              </div>
                              <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                <input
                                  className='form-check-input'
                                  type='checkbox'
                                  checked={
                                    values?.status?.length === getFilteredStatusOptions()?.length
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFieldValue(
                                        'status',
                                        getFilteredStatusOptions()?.map((option) => option.value)
                                      )
                                    } else {
                                      setFieldValue('status', [])
                                    }
                                  }}
                                />
                                <label className='form-check-label' htmlFor='flexRadioLg1'>
                                  All
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className='row g-4 g-xl-4'>
                            <div className='col-sm-12 col-md-12 col-lg-12'>
                              <div className='w-100'>
                                <Select
                                  {...formik.getFieldProps('status')}
                                  className='react-select-styled react-select-solid react-select-sm'
                                  classNamePrefix='react-select'
                                  value={
                                    getFilteredStatusOptions()?.filter(
                                      (option: any) =>
                                        Array.isArray(values.status) &&
                                        (values.status as string[]).includes(option.value)
                                    ) || []
                                  }
                                  onChange={(selectedOptions: any[]) => {
                                    setFieldValue(
                                      'status',
                                      selectedOptions
                                        ? selectedOptions.map(
                                            (option: {value: any}) => option.value
                                          )
                                        : []
                                    )
                                  }}
                                  options={getFilteredStatusOptions()}
                                  isMulti
                                  placeholder='Select Status'
                                  styles={{
                                    control: (provided: any) => ({
                                      ...provided,
                                      width: '100%',
                                      maxHeight: '300px',
                                      overflow: 'auto',
                                      border: '1px solid #e4e6ef',
                                    }),
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Canceled Status Dropdown */}
                      {showCanceledStatusDropdown && (
                        <div className='col-sm-12 col-md-12'>
                          <div className='mb-3'>
                            <div className='col-md-12'>
                              <div className='d-flex justify-content-between align-items-center'>
                                <div>
                                  <label htmlFor='status' className='form-label fs-7 mb-1'>
                                    Canceled Status
                                  </label>
                                </div>
                                <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={
                                      values?.canceledStatus?.length ===
                                      canceledStatusOptions?.length
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldValue(
                                          'canceledStatus',
                                          canceledStatusOptions?.map((option) => option.value)
                                        )
                                      } else {
                                        setFieldValue('canceledStatus', [])
                                      }
                                    }}
                                  />
                                  <label className='form-check-label' htmlFor='flexRadioLg1'>
                                    All
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className='row g-4 g-xl-4'>
                              <div className='col-sm-12 col-md-12 col-lg-12'>
                                <div className='w-100'>
                                  <Select
                                    {...formik.getFieldProps('canceledStatus')}
                                    className='react-select-styled react-select-solid react-select-sm'
                                    classNamePrefix='react-select'
                                    value={
                                      canceledStatusOptions?.filter(
                                        (option: any) =>
                                          Array.isArray(values.canceledStatus) &&
                                          (values.canceledStatus as string[]).includes(option.value)
                                      ) || []
                                    }
                                    onChange={(selectedOptions: any[]) => {
                                      setFieldValue(
                                        'canceledStatus',
                                        selectedOptions
                                          ? selectedOptions.map(
                                              (option: {value: any}) => option.value
                                            )
                                          : []
                                      )
                                    }}
                                    options={canceledStatusOptions}
                                    isMulti
                                    placeholder='Select Status'
                                    styles={{
                                      control: (provided: any) => ({
                                        ...provided,
                                        width: '100%',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        border: '1px solid #e4e6ef',
                                      }),
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AI VOS Status Dropdown */}
                      {showAiVosStatusDropdown && (
                        <div className='col-sm-12 col-md-12'>
                          <div className='mb-3'>
                            <div className='col-md-12'>
                              <div className='d-flex justify-content-between align-items-center'>
                                <div>
                                  <label htmlFor='aiVosStatus' className='form-label fs-7 mb-1'>
                                    AI VOS Status
                                  </label>
                                </div>
                                <div className='form-check form-check-custom form-check-solid form-check-sm mb-2'>
                                  <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={
                                      values?.aiVosStatus?.length ===
                                      aiVosStatusOptions?.length
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setFieldValue(
                                          'aiVosStatus',
                                          aiVosStatusOptions?.map((option) => option.value)
                                        )
                                      } else {
                                        setFieldValue('aiVosStatus', [])
                                      }
                                    }}
                                  />
                                  <label className='form-check-label' htmlFor='flexRadioLg1'>
                                    All
                                  </label>
                                </div>
                              </div>
                            </div>

                            <div className='row g-4 g-xl-4'>
                              <div className='col-sm-12 col-md-12 col-lg-12'>
                                <div className='w-100'>
                                  <Select
                                    {...formik.getFieldProps('aiVosStatus')}
                                    className='react-select-styled react-select-solid react-select-sm'
                                    classNamePrefix='react-select'
                                    value={
                                      aiVosStatusOptions?.filter(
                                        (option: any) =>
                                          Array.isArray(values.aiVosStatus) &&
                                          (values.aiVosStatus as string[]).includes(option.value)
                                      ) || []
                                    }
                                    onChange={(selectedOptions: any[]) => {
                                      setFieldValue(
                                        'aiVosStatus',
                                        selectedOptions
                                          ? selectedOptions.map(
                                              (option: {value: any}) => option.value
                                            )
                                          : []
                                      )
                                    }}
                                    options={aiVosStatusOptions}
                                    isMulti
                                    placeholder='Select AI VOS Status'
                                    styles={{
                                      control: (provided: any) => ({
                                        ...provided,
                                        width: '100%',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        border: '1px solid #e4e6ef',
                                      }),
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Date Range Dropdown */}
                      <div className='fv-row'>
                        <div className='row'>
                          <div className='col-sm-6'>
                            <label htmlFor='startDate' className='form-label fs-7 mb-1'>
                              Start Date
                            </label>
                            <input
                              {...formik.getFieldProps('startDate')}
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              value={values.startDate}
                              onChange={(e) => setFieldValue('startDate', e.target.value)}
                              placeholder='Enter Subject'
                            />
                          </div>
                          <div className='col-sm-6'>
                            <label htmlFor='endDate' className='form-label fs-7 mb-1'>
                              End Date
                            </label>
                            <input
                              {...formik.getFieldProps('endDate')}
                              type='date'
                              className='form-control form-control-white form-select-sm custom-input-height'
                              value={values.endDate}
                              onChange={(e) => setFieldValue('endDate', e.target.value)}
                              placeholder='Start Time'
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='text-end pt-5 px-4'>
                    <button
                      type='reset'
                      className='btn btn-sm btn-light me-2'
                      data-kt-menu-dismiss='true'
                      onClick={handleReset}
                    >
                      Reset
                    </button>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-kt-menu-dismiss='true'
                      onClick={(e) => handleSubmit}
                      disabled={isSubmitting || !isValid || !dirty}
                    >
                      Apply
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </Dropdown.Menu>
        )}
      </Dropdown>
    </>
  )
}
function resetForm() {}
